/**
 * MotifManager.js
 * Manages the generation and transformation of global melodic themes.
 * Ensures the composition has a cohesive "Subject" rather than random noodles.
 */

import { SCALE_INTERVALS, NOTES } from '../../data/theoryConstants.js';

export class MotifManager {
    constructor(options = {}) {
        this.rootKey = options.rootKey || 'C';
        this.scaleType = options.scaleType || 'major';
        this.timeSig = options.timeSig || 4;
        this.random = options.random; // Helper PRNG
        this.config = options.config || {}; // Store full config

        // Section Theme Storage
        this.sectionMotifs = {};
        this.history = {}; // { VERSE: [motif], CHORUS: [motif] }
    }

    /**
     * Resets the manager state for a new generation session.
     * Used by the Optimization Loop to clear cached motifs when rerolling seed.
     * @param {Object} random - New random generator instance
     */
    reset(random) {
        this.sectionMotifs = {};
        this.history = {};
        if (random) this.random = random;
        console.log("[MotifManager] Reset complete. Memory cleared.");
    }

    /**
     * Task 3: Memory Palace (Motif Persistence)
     * Stores a generated motif for a section type.
     * Use simple keys like 'VERSE', 'CHORUS' regardless of 'VERSE_A' etc.
     */
    archiveMotif(sectionType, motif) {
        if (!motif) return;
        // Normalize section type to base type (e.g., VERSE_A -> VERSE)
        // Actually, sometimes we want specific recall.
        // Let's store both exact and base.
        const baseType = sectionType.split('_')[0];

        this.sectionMotifs[sectionType] = motif;
        this.history[sectionType] = motif;

        if (!this.history[baseType]) {
            this.history[baseType] = motif;
        }
    }

    /**
     * Retrieves a stored motif for a section type.
     * Implements "Recall" logic: If current is Chorus, try to recall Verse?
     * Or just simple retrieval.
     */
    getStoredMotif(sectionType) {
        // 1. Exact Match
        if (this.history[sectionType]) return this.deepClone(this.history[sectionType]);

        // 2. Base Match (e.g. VERSE_B -> VERSE)
        const baseType = sectionType.split('_')[0];
        if (this.history[baseType]) return this.deepClone(this.history[baseType]);

        // 3. Fallback Recall Logic (Task 3)
        // If Outro, recall Chorus or Intro
        if (sectionType.includes('OUTRO')) {
             if (this.history['CHORUS']) return this.deepClone(this.history['CHORUS']);
             if (this.history['INTRO']) return this.deepClone(this.history['INTRO']);
        }

        // Fix: Epic/Cinematic Structure Chaining (Intro -> Build -> Climax)
        if (sectionType.includes('BUILD')) {
             if (this.history['INTRO']) return this.deepClone(this.history['INTRO']);
        }
        if (sectionType.includes('CLIMAX') || sectionType.includes('DROP')) {
             if (this.history['BUILD']) return this.deepClone(this.history['BUILD']);
             if (this.history['INTRO']) return this.deepClone(this.history['INTRO']);
             if (this.history['VERSE']) return this.deepClone(this.history['VERSE']);
        }

        return null;
    }

    /**
     * Deep clones a motif array to prevent reference mutations.
     */
    deepClone(motif) {
        if (!motif) return null;
        return motif.map(n => ({...n}));
    }

    /**
     * Generates a "Consequent" phrase based on an "Antecedent" motif.
     * Logically resolves the rhythmic or melodic tension.
     * @param {Array} antecedent - Source motif events
     */
    generateConsequent(antecedent) {
        if (!antecedent || antecedent.length === 0) return [];

        // Strategy:
        // 1. Rhythmic Complement: Keep rhythm, reverse contour?
        // 2. Rhythmic Resolution: Simplify the end of the phrase.
        // 3. Invert contour.

        // Simple implementation: Inversion + Rhythmic simplification at end
        let consequent = antecedent.map(ev => ({ ...ev }));

        // Invert Contour (Scale Degree based)
        const startNote = consequent[0].scaleDegree || 0; // Assuming scale degrees exist
        consequent = consequent.map(n => ({
            ...n,
            scaleDegree: n.scaleDegree !== undefined ? (startNote - (n.scaleDegree - startNote)) : n.scaleDegree
        }));

        // Simplify Tail (Resolution)
        // If last few notes are short, make them long
        if (consequent.length > 2) {
            const last = consequent[consequent.length-1];
            if (last.len < 1.0) {
                // Extend last note
                last.len = 1.0;
                // Remove penultimate if it overlaps?
                // Simplified: Just extend duration.
            }
        }

        return consequent;
    }

    /**
     * Returns just the rhythmic pattern of the motif for use by Bass/Comping.
     * @param {string} sectionType
     * @returns {Array} Array of {s, l} objects.
     */
    getMotifRhythm(sectionType) {
        const motif = this.getMotifForSection(sectionType);
        if (!motif) return [];
        return motif.map(note => ({ s: note.s, l: note.len }));
    }

    /**
     * Retrieves or generates a motif for a specific section.
     */
    getMotifForSection(sectionType) {
        if (this.sectionMotifs[sectionType]) {
            return this.sectionMotifs[sectionType];
        }

        // Try to inherit from base/history
        const inherited = this.getStoredMotif(sectionType);
        if (inherited) {
             // Clone exactly (Inheritance)
             // We store the CLONE as this section's motif.
             this.sectionMotifs[sectionType] = inherited;
             this.archiveMotif(sectionType, inherited);
             return inherited;
        }

        // Generate Fresh
        const fresh = this.generateMotif(sectionType);
        this.sectionMotifs[sectionType] = fresh;
        this.archiveMotif(sectionType, fresh);
        return fresh;
    }

    /**
     * Generates a "Seed" motif.
     * 1-2 bars long, distinct contour.
     * @param {string} sectionType
     * @param {Object} context - Optional context with config (for anchors)
     */
    generateMotif(sectionType, context = {}) {
        // Use context.config if provided, else fall back to this.config
        const cfg = context.config || this.config;

        // Configurable Anchors
        const anchors = (cfg.mutationProfile && cfg.mutationProfile.anchorPoints)
            ? cfg.mutationProfile.anchorPoints
            : [0]; // Default to Downbeat

        // 1. Generate Rhythm (Generative Grammar)
        // Adjust density based on section
        const isDense = sectionType.includes('CHORUS') || sectionType.includes('CLIMAX');

        let atoms = [
            [{l: 1.0}], // Quarter
            [{l: 0.5}, {l: 0.5}], // Two 8ths
            [{l: 1.5}, {l: 0.5}], // Dotted 8th + 16th
            [{l: 0.75}, {l: 0.25}], // Dotted 8th + 16th
            [{l: 2.0}] // Half note
        ];

        if (isDense) {
            // Add denser atoms
            atoms.push([{l: 0.25}, {l: 0.25}, {l: 0.25}, {l: 0.25}]); // 16ths
            atoms.push([{l: 0.33}, {l: 0.33}, {l: 0.33}]); // Triplets
            // Remove long notes to encourage movement
            atoms = atoms.filter(a => a[0].l < 2.0);
        }

        const selectedRhythm = [];
        let currentBeat = 0;
        const totalDuration = 4.0; // 1 Bar Motif for now (or 2?)

        // Fill 4 beats
        while (currentBeat < totalDuration) {
            const remaining = totalDuration - currentBeat;

            // Force Anchor Logic:
            // If we are approaching an anchor point, ensure we land ON it or span OVER it cleanly?
            // "Anchor" usually means "Note ONSET at this time".
            // So if currentBeat is an anchor, we MUST start a note here (which we do by default as we fill sequentially).
            // But we must ensure we don't pick an atom that skips a *future* anchor if we want strict locking?
            // Actually, atoms always start at currentBeat.
            // So the only risk is if we pick a long note (e.g. 2.0) at beat 0, effectively covering beat 1.
            // If beat 1 is an anchor, we failed to onset on beat 1.

            // Filter atoms that would overshoot an anchor point without landing on it?
            // No, atoms are onset at `currentBeat`.
            // So if `currentBeat` is 0, and we pick length 2.0, next `currentBeat` is 2.0.
            // If Anchor is at 1.0, we missed it.

            // Valid Atoms Logic:
            // Find next anchor > currentBeat.
            // Ensure atom length <= (nextAnchor - currentBeat) IF we want to force onset at next anchor.

            // Let's implement strict anchor adherence for "Tightness".
            let maxLen = remaining + 0.01;

            // Find nearest future anchor
            const nextAnchor = anchors.find(a => a > currentBeat && a < totalDuration);
            if (nextAnchor !== undefined) {
                 // Restrict max length to hit the anchor
                 maxLen = nextAnchor - currentBeat + 0.01;
            }

            // Pick an atom that fits
            // Filter atoms where sum(l) <= remaining AND sum(l) <= maxLen (if constrained)
            const validAtoms = atoms.filter(atom => {
                const len = atom.reduce((sum, n) => sum + n.l, 0);
                // Allow exact match or smaller
                return len <= maxLen;
            });

            // If strict anchor logic filtered everything (e.g. only have 0.5 atoms but need 0.25), fallback to standard remaining check
            const pool = validAtoms.length > 0 ? validAtoms : atoms.filter(atom => {
                 const len = atom.reduce((sum, n) => sum + n.l, 0);
                 return len <= remaining + 0.01;
            });

            if (pool.length === 0) break;

            const atom = this.random ? this.random.pick(pool) : pool[Math.floor(Math.random() * pool.length)];

            // Add atom to rhythm
            let localOffset = 0;
            atom.forEach(note => {
                selectedRhythm.push({
                    s: currentBeat + localOffset,
                    l: note.l
                });
                localOffset += note.l;
            });

            currentBeat += localOffset;
        }

        // 2. Generate Pitch Contour (Scale Degrees)
        // Start on 1 or 5. End on 1, 3, or 5.
        const startDegree = this.random ? (this.random.bool() ? 0 : 4) : (Math.random() > 0.5 ? 0 : 4);

        const scaleDegrees = [startDegree];
        let current = startDegree;

        // Determine Global Contour for this Subject
        const contourTypes = ['arch', 'wave', 'ascending', 'descending'];
        const contour = this.random ? this.random.pick(contourTypes) : contourTypes[Math.floor(Math.random() * contourTypes.length)];

        // Track previous interval for Gap Fill logic
        let lastInterval = 0;

        for(let i=1; i<selectedRhythm.length; i++) {
            // Determine range of valid steps based on Contour
            let minStep = -2;
            let maxStep = 2;

            const progress = i / selectedRhythm.length;

            if (contour === 'ascending') {
                 // Favor upward movement
                 minStep = -1;
                 maxStep = 3;
            } else if (contour === 'descending') {
                 // Favor downward movement
                 minStep = -3;
                 maxStep = 1;
            } else if (contour === 'arch') {
                 // Up first half, Down second half
                 if (progress < 0.5) {
                     minStep = -1; maxStep = 3;
                 } else {
                     minStep = -3; maxStep = 1;
                 }
            } else if (contour === 'wave') {
                 // Up, Down, Up, Down
                 if (i % 4 < 2) {
                     minStep = -1; maxStep = 2;
                 } else {
                     minStep = -2; maxStep = 1;
                 }
            }

            // Generate Step
            let step = this.random ? (this.random.range(minStep, maxStep + 1)) : (Math.floor(Math.random() * (maxStep - minStep + 1)) + minStep);

            // Avoid consecutive zeros (Repeated notes)
            if (step === 0 && lastInterval === 0) {
                // Force movement
                step = (this.random ? this.random.next() : Math.random()) > 0.5 ? 1 : -1;
            }

            // --- GAP FILL LOGIC ---
            // If previous interval was a large leap (> 4 semitones approx 3 scale steps),
            // force direction reversal.
            if (Math.abs(lastInterval) >= 3) {
                 if (lastInterval > 0) {
                     // Was Leap Up -> Step Down
                     step = this.random ? this.random.range(-2, 0) : -1;
                 } else {
                     // Was Leap Down -> Step Up
                     step = this.random ? this.random.range(1, 3) : 1;
                 }
            }

            // Apply Step
            current += step;
            scaleDegrees.push(current);
            lastInterval = step;
        }

        // 3. Convert to MIDI (Relative to C4=60 + Key)
        const scaleIntervals = SCALE_INTERVALS[this.scaleType] || SCALE_INTERVALS.major;
        const rootIdx = NOTES.indexOf(this.rootKey);

        if (rootIdx === -1) {
            console.warn('[MotifManager] Invalid rootKey:', this.rootKey);
            return;
        }

        return selectedRhythm.map((r, idx) => {
            const degree = scaleDegrees[idx];
            // Normalize degree to 0-7 range
            const octave = Math.floor(degree / 7) + 4; // Base octave 4
            const scaleStep = ((degree % 7) + 7) % 7;
            const interval = scaleIntervals[scaleStep];

            const pitch = (octave * 12) + rootIdx + interval;

            return {
                s: r.s,
                len: r.l,
                d: pitch,
                scaleDegree: degree // Store for transformations
            };
        });
    }

    /**
     * Returns a fragmentation of the motif.
     * @param {string} type - 'head' (first 33%), 'tail' (last 33%), 'core' (middle).
     * @param {Array} sourceMotif - The motif events.
     */
    getFragment(type, sourceMotif) {
        if (!sourceMotif || sourceMotif.length === 0) return [];
        const len = sourceMotif.length;
        // Need at least 3 notes to fragment meaningfully
        if (len < 3) return sourceMotif;

        const third = Math.floor(len / 3);
        const splitPoint1 = Math.max(1, third);
        const splitPoint2 = Math.max(2, len - third);

        if (type === 'head') {
            return sourceMotif.slice(0, splitPoint2); // First 2/3rds? Or just first 1/3?
            // "Head" usually means the recognizable start. Let's say first half.
            return sourceMotif.slice(0, Math.ceil(len/2));
        } else if (type === 'tail') {
            return sourceMotif.slice(Math.floor(len/2));
        } else if (type === 'rhythm_only') {
            return sourceMotif.map(n => ({ s: n.s, l: n.len })); // Strip pitch
        }
        return sourceMotif;
    }

    /**
     * Scales the rhythm of the motif.
     * @param {number} factor - 2.0 (slower), 0.5 (faster)
     * @param {Array} motif - Source motif
     */
    scaleRhythm(motif, factor) {
        if (!motif) return [];
        return motif.map(n => ({
            ...n,
            s: n.s * factor,
            len: n.len * factor
        }));
    }

    /**
     * Re-calculates MIDI pitches from Scale Degrees to match context.
     * Useful after transformation.
     */
    recalculatePitches(motifEvents) {
        const scaleIntervals = SCALE_INTERVALS[this.scaleType] || SCALE_INTERVALS.major;
        const rootIdx = NOTES.indexOf(this.rootKey);

        if (rootIdx === -1) return motifEvents;

        return motifEvents.map(ev => {
            if (ev.scaleDegree === undefined) return ev;

            const degree = Math.round(ev.scaleDegree);
            const octave = Math.floor(degree / 7) + 4;
            const scaleStep = ((degree % 7) + 7) % 7;
            const interval = scaleIntervals[scaleStep];
            const pitch = (octave * 12) + rootIdx + interval;

            return { ...ev, d: pitch };
        });
    }

    /**
     * Returns a variation of a motif.
     * @param {string} type - Variation type
     * @param {string} sectionType - Section to vary (optional, defaults to provided motif or error)
     * @param {Array} sourceMotif - Optional direct source
     * @param {Object} context - Context for accessing mutationRate
     */
    getVariation(type, sectionType = 'VERSE', sourceMotif = null, context = {}) {
        const source = sourceMotif || this.getMotifForSection(sectionType);
        if (!source || source.length === 0) return [];

        let result = [...source];

        // Use context.config if provided, else fall back to this.config
        const cfg = context.config || this.config;

        // Clamped Mutation Rates (Identity Protection)
        // If mutationRate is low, force 'identity' variation regardless of requested type
        // unless explicitly overridden.
        const mutationRate = (cfg.mutationProfile && cfg.mutationProfile.mutationRate !== undefined)
            ? cfg.mutationProfile.mutationRate
            : 1.0; // Default open

        // Check if we should suppress variation to protect identity
        const shouldMutate = this.random ? (this.random.next() < mutationRate) : (Math.random() < mutationRate);

        if (!shouldMutate && type !== 'identity') {
            // Force identity (Clone)
            return this.recalculatePitches(result);
        }

        // --- ROUND 5 IMPROVEMENT: Configurable Anchor Points ---
        // Store seed anchor notes (pitches at defined beats) to restore them after mutation
        const anchors = (cfg.mutationProfile && cfg.mutationProfile.anchorPoints)
            ? cfg.mutationProfile.anchorPoints
            : [0, 2]; // Default Beats 1 and 3

        const anchorMap = {}; // { beat: scaleDegree }
        source.forEach(note => {
            // Check if note hits an anchor (tolerance 0.1)
            anchors.forEach(a => {
                if (Math.abs(note.s - a) < 0.1) {
                    anchorMap[a] = note.scaleDegree;
                }
            });
        });

        // Advanced Variations (Phase 2 Expansion)
        // 1. Thematic Styles
        if (type === 'heroic') {
            // "Heroic": Larger intervals, dotted rhythm, strong beat emphasis.
            // Force 4ths/5ths in contour?
            // Augment rhythm slightly?
            // Just use inversion + augmentation?

            // Simplification: Shift up an octave, ensure strong downbeats.
            result = result.map(n => ({
                ...n,
                scaleDegree: n.scaleDegree + 7, // Up octave
                vel: 1.1 // Louder
            }));
            // Snap rhythm to grid?
            // Let's assume input rhythm is good.
        }
        else if (type === 'melancholy') {
            // "Melancholy": Slower, lower octave, flattened contour.
            result = this.scaleRhythm(result, 1.5); // Slower
            result = result.map(n => ({
                ...n,
                scaleDegree: n.scaleDegree - 3, // Shift down
                vel: 0.7 // Softer
            }));
            // Flatten contour: Reduce interval leaps
            // (Complex to do without recreating. Just shifting down is okay for now)
        }
        else if (type === 'agitated') {
            // "Agitated": Fast, repeated notes, fragmented.
            // Take the HEAD only.
            result = this.getFragment('head', result);
            // Diminish (double speed)
            result = this.scaleRhythm(result, 0.5);

            // Loop to fill at least 4 bars (assuming standard section)
            const baseDur = result.length > 0 ? (result[result.length-1].s + result[result.length-1].len) : 0;
            if (baseDur > 0) {
                const clones = [];
                let currentDur = baseDur;
                const targetDur = 4.0; // Fill 1 bar (or 4?)
                // If baseDur is 0.5 beats?
                // Motif is usually 4 beats. Head is 2. Scaled is 1 beat?
                // Let's assume we want to fill 4 beats.

                // Safety limiter
                let loops = 0;
                while (currentDur < 8.0 && loops < 16) {
                    const offset = currentDur;
                    const shifted = result.map(n => ({...n, s: n.s + offset}));
                    clones.push(...shifted);
                    currentDur += baseDur;
                    loops++;
                }
                result = [...result, ...clones];
            }
        }
        else {
            // Standard Transformations
            switch(type) {
                case 'thematic_transformation':
                    result = result.map(n => ({ ...n, scaleDegree: n.scaleDegree + 2 }));
                    break;
                case 'inversion':
                    const startNote = result[0].scaleDegree;
                    result = result.map(n => ({ ...n, scaleDegree: startNote - (n.scaleDegree - startNote) }));
                    break;
                case 'retrograde':
                    const pitches = result.map(n => n.scaleDegree).reverse();
                    result = result.map((n, i) => ({ ...n, scaleDegree: pitches[i] }));
                    break;
                case 'augmentation':
                    result = this.scaleRhythm(result, 2.0);
                    break;
                 case 'diminution':
                    result = this.scaleRhythm(result, 0.5);
                    break;
                case 'identity':
                default:
                    break;
            }
        }

        // --- Restore Anchors ---
        // If anchor notes were mutated, force them back to original scale degree?
        // Only if type is 'thematic_transformation' or 'random'?
        // Inversion/Retrograde explicitly change shape, so anchors might break logic there.
        // But for "Mutation" (random jitter), we want to lock anchors.
        // Let's enforce for 'thematic_transformation' and 'agitated' or generic mutations.
        const enforceAnchors = ['thematic_transformation', 'heroic', 'melancholy'].includes(type) || shouldMutate;

        if (enforceAnchors) {
            result = result.map(note => {
                // Check if this note is at an anchor point
                let matchingAnchor = null;
                anchors.forEach(a => {
                    if (Math.abs(note.s - a) < 0.1) matchingAnchor = a;
                });

                if (matchingAnchor !== null && anchorMap[matchingAnchor] !== undefined) {
                    // Restore original Scale Degree
                    return { ...note, scaleDegree: anchorMap[matchingAnchor] };
                }
                return note;
            });
        }

        return this.recalculatePitches(result);
    }

    /**
     * Adapt notes to the current harmony.
     * Moves strong-beat notes to the nearest chord tone.
     * @param {Array} motifNotes - Array of note events.
     * @param {Object} chord - Current chord object (rootPitchClass, type, notes).
     */
    adaptMotifToHarmony(motifNotes, chord) {
        if (!motifNotes || !chord) return motifNotes;

        // Get chord tones (Pitch Classes)
        // If chord.notes (MIDI) exists, use % 12. Else calculate from formula.
        const chordTones = chord.notes ? chord.notes.map(n => n % 12) : [];
        if (chordTones.length === 0) return motifNotes;

        return motifNotes.map(ev => {
            // Check if on strong beat (1, 2, 3, 4 in 4/4)
            // Or just Beat 1 and 3?
            // Let's assume Beat 1 (0.0) and Beat 3 (2.0) are strong.
            const beatPos = ev.s % 4; // Assuming 4/4
            const isStrong = (Math.abs(beatPos % 1) < 0.1); // On any beat

            if (isStrong) {
                // Find nearest chord tone
                const currentPC = ev.d % 12;
                if (!chordTones.includes(currentPC)) {
                    // Shift
                    let bestDiff = Infinity;
                    let bestNote = ev.d;

                    chordTones.forEach(ct => {
                        // Find closest octave of ct to ev.d
                        // ev.d = 64 (E4). ct = 0 (C)
                        // cands: 60, 72. 60 is diff 4.

                        // Naive closest calc
                        const currentOctave = Math.floor(ev.d / 12);
                        [-1, 0, 1].forEach(offset => {
                             const cand = ct + (currentOctave + offset) * 12;
                             const diff = Math.abs(cand - ev.d);
                             if (diff < bestDiff) {
                                 bestDiff = diff;
                                 bestNote = cand;
                             }
                        });
                    });

                    // Limit movement to max 2 semitones?
                    // If jump is too big, maybe it's better to stay?
                    // No, for "Harmonic Locking", we jump.
                    return { ...ev, d: bestNote };
                }
            }
            return ev;
        });
    }
}
